import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../services/app-insights.service";
import { useWorkflowIntelligence } from "../contexts/WorkflowIntelligenceContext";

/**
 * Emits an App Insights pageView every time the react-router location
 * changes. We need this because the addin uses `MemoryRouter` — routing
 * lives entirely in memory, `window.location` never changes, and the
 * SDK's `enableAutoRouteTracking` (which hooks `pushState`/`popState`)
 * never sees a route change.
 *
 * Mount via a tiny `<RouteTracker />` component inside the Router — the
 * hook only works when there's a `<MemoryRouter>` (or any router)
 * ancestor providing `useLocation`.
 *
 * Effect runs on `location.pathname` changes only. We deliberately don't
 * depend on `userRole` or `spreadsheetId` — changing those mid-session
 * shouldn't emit a duplicate pageView. The latest values are stamped at
 * the moment of the next route change; in-between, custom events
 * (via useTrackEvent) already capture current context.
 */
export function useTrackPageViews(): void {
    const { userRole, spreadsheetId } = useWorkflowIntelligence();
    const location = useLocation();

    useEffect(() => {
        trackPageView(location.pathname, {
            userRole,
            ...(spreadsheetId ? { spreadsheetId } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
}
